import { Platform } from 'quasar'

import AdminsRoutes from './routes/admins'
import SuperAdminRoutes from './routes/superAdmin'

// Guards para el modulo solo-movil de Exhibiciones
import { mobileOnlyGuard } from './guards/mobileOnlyGuard'
import { webOnlyGuard } from './guards/webOnlyGuard'
import { geolocationGuard } from './guards/geolocationGuard'
import { exhibitionSettingsGuard } from './guards/exhibitionSettingsGuard'
import { controlTowerGuard } from './guards/controlTowerGuard'

const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('pages/LoginPage.vue')
  },
  {
    path: '/reset-password/:email/:id/:token',
    name: 'reset-password',
    component: () => import('pages/ResetPassword.vue')
  },
  {
    path: '/sms-password-reset',
    name: 'sms-password-reset',
    component: () => import('pages/ResetPassword.vue'),
    meta: { requiresAuth: true },
    beforeEnter: () => {
      if (sessionStorage.getItem('smsPasswordResetPending') === '1') return true
      return { name: 'login' }
    }
  },
  {
    path: '/surveyOccurrence/:token',
    name: 'survey',
    component: () => import('pages/public/survey/SurveyOccurrence.vue'),
    meta: { public: true }
  },
  {
    path: '/pdf-descarga/:token',
    name: 'pdf-download',
    component: () => import('pages/PdfDownload.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/surveyThanks',
    name: 'thanks',
    component: () => import('pages/public/survey/SurveyThanks.vue'),
    meta: { public: true }
  },
  {
    path: '/information/InformationNews',
    name: 'news',
    component: () => import('pages/Information.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: () => import('layouts/MainLayout.vue'),
    meta: {
      requiresAuth: true
    },
    children: [
      { path: '', name: 'navigation.home', component: () => import('pages/NewDashboard.vue') },
      { path: 'dashboard', name: 'navigation.dashboard', component: () => import('pages/Dashboard.vue') },
      { path: 'dashboard/settings', name: 'navigation.dashboard-settings', component: () => import('pages/user/DashboardSettings.vue') },
      { path: 'profile', name: 'navigation.user.profile', component: () => import('pages/user/UserProfile.vue') },
      { path: 'profile/api-keys', name: 'navigation.user.api_keys', component: () => import('pages/user/ApiKeys.vue') },
      {
        path: 'occurrences',
        name: 'navigation.occurrences',
        component: () => import('pages/user/occurrences/Forms.vue'),
        children: [
          { path: ':id', name: 'navigation.occurrence_fill', component: () => import('pages/user/occurrences/Occurrence.vue') }
        ]
      },
      {
        path: 'tasks',
        name: 'navigation.action_plans.index',
        component: () => import('pages/user/actionPlans/Tasks.vue')
      },
      {
        path: 'tasks/:id',
        name: 'navigation.action_plans.show',
        component: () => import('pages/user/actionPlans/Task.vue')
      },
      {
        path: 'missions',
        name: 'navigation.missions.index',
        component: () => import('pages/user/actionPlans/RuickCheck.vue')
      },
      {
        path: 'reports',
        component: () => import('pages/user/reports/Reports.vue'),
        children: [
          { path: '', name: 'navigation.reports.index', component: () => import('pages/user/reports/Result.vue') }
        ]
      },
      {
        path: 'calendar',
        name: 'navigation.calendar',
        component: () => import('pages/user/calendar/NewCalendar.vue'),
        props: route => ({
          initialView: route.query.view || null
        })
      },
      {
        path: 'gallery',
        component: () => import('pages/user/photosGallery/index.vue'),
        children: [
          { path: '', name: 'navigation.photosGallery.dashboard', component: () => import('pages/user/photosGallery/GalleryDashboard.vue') },
          {
            path: '/gallerySprint/:id',
            name: 'navigation.photosGallery.SprintGallery',
            component: () => import('src/components/photosGallery/SprintGallery.vue')
          },
          {
            path: '/galleryForm/:id',
            name: 'navigation.photosGallery.FormGallery',
            component: () => import('src/components/photosGallery/FormGallery.vue')
          }
        ]
      },
      // Resources
      { path: 'resources', name: 'navigation.resources.index', component: () => import('pages/resources/Index.vue') },
      { path: 'resources/:id', name: 'navigation.resources.show', component: () => import('pages/resources/Group.vue') },
      //Notices
      { path: 'notices', name: 'navigation.notices.index', component: () => import('pages/user/notices/Index.vue') },
      // Control Tower
      {
        path: 'ControlTower',
        name: 'navigation.control_tower.index',
        component: () => import('@/pages/user/controlTower/ControlTowerPage.vue'),
        // TKT-2234 F01: solo allowlist de correos (admin@espresso.com por defecto)
        beforeEnter: controlTowerGuard
      },
      // Exhibitions (solo-movil: bloqueado en web via mobileOnlyGuard)
      {
        path: 'exhibitions',
        name: 'navigation.exhibitions.index',
        component: () => import('pages/user/ExhibitionListPage.vue'),
        beforeEnter: mobileOnlyGuard
      },
      {
        path: 'exhibitions/new',
        name: 'navigation.exhibitions.new',
        component: () => import('pages/user/ExhibitionFormPage.vue'),
        // Precarga la geolocalizacion para preseleccionar la ubicacion.
        // En dev (web) se permite entrar para pruebas; en produccion es solo-movil.
        beforeEnter: (to, from, next) => {
          if (!Platform.is.capacitor && !import.meta.env.DEV) return next({ name: 'navigation.home' })
          return geolocationGuard(to, from, next, 'route:exhibitions/new')
        }
      },
      {
        // Configuracion de catalogos (mobile-only + permiso assign-exhibitions).
        // Antes de :id para que 'settings' no lo capture el wildcard.
        path: 'exhibitions/settings',
        name: 'navigation.exhibitions.settings',
        component: () => import('pages/user/ExhibitionSettingsPage.vue'),
        beforeEnter: exhibitionSettingsGuard
      },
      {
        path: 'exhibitions/:id',
        name: 'navigation.exhibitions.show',
        component: () => import('pages/user/ExhibitionDetailPage.vue'),
        beforeEnter: mobileOnlyGuard
      },
      {
        path: 'exhibitions/:id/details',
        name: 'navigation.exhibitions.details',
        component: () => import('pages/user/ExhibitionLogPage.vue'),
        beforeEnter: mobileOnlyGuard
      },
      // Caducities
      { path: 'caducities', name: 'navigation.caducities.index', component: () => import('pages/user/caducities/index.vue') },
      {
        path: 'routines',
        name: 'navigation.routines.index',
        component: () => import('pages/user/routines/index.vue'),
        meta: { permission: 'routines' }
      },
      {
        path: 'Analytics',
        name: 'navigation.dashboard2.index',
        component: () => import('pages/user/DashboardMetabase/Dashboard2.vue')
      },
      {
        path: 'analytics-dashboards',
        name: 'navigation.analytics-dashboards.index',
        component: () => import('pages/user/PowerBiReportsPage.vue'),
        beforeEnter: webOnlyGuard
      },
      AdminsRoutes
    ]
  },
  SuperAdminRoutes
]

// Playground de testing offline — SOLO en local (oculto, no aparece en menús).
if (import.meta.env.APP_ENV === 'LOCAL') {
  // Playground offline-v2 (SQLite) — spike de reactividad (UI-01): optimistic + post-sync re-query
  // via offline-v2/domain + useReactiveQuery. SOLO local, oculto, no aparece en menus.
  routes.push({
    path: '/dev/offline-v2-playground',
    name: 'dev.offline-v2-playground',
    component: () => import('pages/dev/OfflineV2Playground.vue')
  })
  // Diagnostico offline-v2 (validacion en dispositivo 08-01): corre los escenarios VAL-02 EN PANTALLA
  // contra el plugin SQLite NATIVO real, asi la matriz se ejecuta telefono-en-mano sin laptop.
  // FRONTERA DE RECLAMO (ORCHESTRATOR-LOCKED, opcion a): TODA la validacion corre en el build DEV/LOCAL;
  // NO hay un punto de entrada oculto en produccion (esta ruta esta gated por APP_ENV===LOCAL → en prod
  // la rama no existe). Las conveniencias DEV (devtools/flag/pantalla) NO tocan la capa de storage
  // (SQLite/FS/WAL nativos byte-identicos a prod), por lo que los resultados valen para prod.
  routes.push({
    path: '/dev/offline-v2-diagnostics',
    name: 'dev.offline-v2-diagnostics',
    component: () => import('pages/dev/OfflineV2Diagnostics.vue')
  })
  // Preview de los presentacionales Field* (T2.2) — SOLO local, oculto, no aparece en menus.
  routes.push({
    path: '/dev/field-preview',
    name: 'dev.field-preview',
    component: () => import('pages/dev/FieldPreviewPage.vue')
  })
}

// Always leave this as last one
if (import.meta.env.QUASAR_MODE !== 'ssr') {
  routes.push({
    path: '/not-found',
    name: 'not-found',
    component: () => import('pages/ErrorPage404.vue')
  })
}

export default routes
